import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import Users from '../../Constants/Endpoints/Users';
import GetUserOpsLogEntriesUsingCurrentTokenRequest from '../../Interfaces/Requests/Users/GetUserOpsLogEntriesUsingCurrentTokenRequest';
import GetUserOpsLogEntriesUsingCurrentTokenResponse from '../../Interfaces/Responses/Users/GetUserOpsLogEntriesUsingCurrentTokenResponse';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import {
    Alert as ServaAlert,
    Button,
    Col,
    Divider,
    Input,
    Option,
    RefreshIcon,
    Row,
    Select,
    Skeleton,
    Text,
    Tooltip
} from '@servahealth/serva-ui-kit';
import '../../Css/Loading.css';

interface AccountUserLogsProps {
    onCancel: () => void;
}

const AccountUserLogs = ({ onCancel }: AccountUserLogsProps) => {
    const { user } = useAuth();

    const [userLogs, setUserLogs] = useState<GetUserOpsLogEntriesUsingCurrentTokenResponse[]>([]);
    const [entryLevel, setEntryLevel] = useState('User');
    const [rowLimit, setRowLimit] = useState('100');

    const { mutate: getUserLogs, isLoading: isGetUserLogsLoading, isError: isGetUserLogsError, error: getUserLogsError, } = useMutation(
        async () => {
            let responseData: Promise<GetUserOpsLogEntriesUsingCurrentTokenResponse[]>;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Users.GET_USER_OPS_LOG_ENTRIES_USING_CURRENT_TOKEN}`;
                const requestData: GetUserOpsLogEntriesUsingCurrentTokenRequest = {
                    guol_token: user?.runningUser?.currentToken.toString() ?? '',
                    guol_entryLevel: entryLevel,
                    guol_count: rowLimit,
                };
                responseData = AxiosUtil.get<GetUserOpsLogEntriesUsingCurrentTokenResponse[], GetUserOpsLogEntriesUsingCurrentTokenRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_USER_OPS_LOG_ENTRIES_USING_CURRENT_TOKEN [Get User Ops Logs] Internal Error: ' + err);
                console.error('GET_USER_OPS_LOG_ENTRIES_USING_CURRENT_TOKEN [Get User Ops Logs] Internal Error: ', err);
                responseData = Promise.reject();
            }
            return responseData;
        },
        {
            onError: (err: any) => {
                setCurrentError('GET_USER_OPS_LOG_ENTRIES_USING_CURRENT_TOKEN [Get User Ops Logs] Internal Error: ' + err);
                console.error('GET_USER_OPS_LOG_ENTRIES_USING_CURRENT_TOKEN [Get User Ops Logs] Internal Error: ', err);
            },
            onSuccess: (response: GetUserOpsLogEntriesUsingCurrentTokenResponse[]) => {
                setUserLogs(response);
            },
        }
    );

    const [currentError, setCurrentError] = useState('');

    useEffect(() => {
        if (user?.runningUser.currentToken) {
            getUserLogs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const determineWhichError = () => {
        if ( isGetUserLogsLoading ) {
            return currentError + ' ' + (getUserLogsError as any).response?.data.message;
        }
        else {
            return '';
        }
    } 

    return (
        <>
            {
                isGetUserLogsError
                &&
                <ServaAlert
                    className={'mb-2'}
                    message={determineWhichError()}
                    showIcon={true}
                    type="error"
                ></ServaAlert>
            }
            <Divider style={{ borderTop: '1px solid black', margin: '8px 0' }}></Divider>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={3} style={{display: 'flex', justifyContent: 'right'}}>
                    <Text italic style={{paddingTop: '8px'}}>Current User:</Text>
                </Col>
                <Col span={8}>
                    <Input
                        readOnly
                        value={user?.email}
                        style={{paddingTop: '4px', width: '100%'}}
                    />
                </Col>
                <Col span={3} style={{display: 'flex', justifyContent: 'right'}}>
                    <Text italic style={{paddingTop: '8px'}}>Running User:</Text>
                </Col>
                <Col span={8}>
                    <Input
                        readOnly
                        value={user?.runningUser?.email}
                        style={{paddingTop: '4px', width: '100%'}}
                    />
                </Col>
                <Col span={2}>
                </Col>
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={3} style={{display: 'flex', justifyContent: 'right'}}>
                    <Text style={{paddingTop: '8px'}}>Entry Level:</Text>
                </Col>
                <Col span={5}>
                    <Select
                        style={{paddingTop: '4px', width: '100%'}}
                        value={entryLevel}
                        onChange={(value: string) => setEntryLevel(value)}
                    >
                        <Option value='User'>User</Option>
                        <Option value='System'>System</Option>
                        <Option value='DataSet'>DataSet</Option>
                    </Select>
                </Col>
                <Col span={3} style={{display: 'flex', justifyContent: 'right'}}>
                    <Text style={{paddingTop: '8px'}}>Rows:</Text>
                </Col>
                <Col span={5}>
                    <Select
                        style={{paddingTop: '4px', width: '100%'}}
                        value={rowLimit}
                        onChange={(value: string) => setRowLimit(value)}
                    >
                        <Option value='100'>100</Option>
                        <Option value='200'>200</Option>
                        <Option value='400'>400</Option>
                        <Option value='800'>800</Option>
                    </Select>
                </Col>
                <Col span={8} />
            </Row>
            <Divider style={{ borderTop: '1px solid black', margin: '8px 0' }}></Divider>
            <div style={{overflow: 'auto', height: '350px', whiteSpace: 'nowrap'}}>
                {
                    isGetUserLogsLoading
                    ?
                    <Skeleton paragraph={{ rows: 12 }} active />
                    :
                    <Row>
                        <Col span={24}>
                            {userLogs.map((eachLog, index) => {
                                const entryMessage = eachLog.entryMessage.trim();
                                const entryDate = eachLog.entryDate.includes('.') ? eachLog.entryDate.substring(0, eachLog.entryDate.indexOf('.')) : eachLog.entryDate;
                                const displayedEntryMessage = entryMessage.length > 1500 ? entryMessage.substring(0, 1500) : entryMessage;
                                const entryParams = eachLog.entryParams.trim();

                                return (
                                    <div
                                        key={`${eachLog.entryDate}-${eachLog.entryLevel}-${index}`}
                                        style={{whiteSpace: 'nowrap', fontFamily: 'Consolas, "Courier New", monospace', fontSize: '14px', lineHeight: '18px'}}
                                    >
                                        {`${entryDate} - ${eachLog.entryIPAddress} - ${eachLog.entryLevel} - [${displayedEntryMessage}] ${entryParams}`}
                                    </div>
                                );
                            })}
                        </Col>
                    </Row>
                }
            </div>
            <br></br>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={4}>
                    <Button
                        block
                        type="default"
                        onClick={onCancel}
                    >
                        Close
                    </Button>
                </Col>
                <Col span={16}>
                </Col>
                <Col span={4}>
                    <Tooltip title={'refresh'}>
                        <Button
                            block
                            icon={<RefreshIcon />}
                            type='primary'
                            onClick={() => getUserLogs()}
                        >
                            Load Logs
                        </Button>
                    </Tooltip>
                </Col>
            </Row>
        </>
    );
};

export default AccountUserLogs;
